import { SelectSortComment } from "@/components/PredictionMarket/SortComment";
import { scrollToComment } from "@/utils";
import { CheckBoxFilledIcon, CheckBoxIcon } from "@roman/shared/components/Icons";
import { useGetParentComments } from "@roman/shared/hooks/PredictionMarket/useGetParentComments";
import { usePostComment } from "@roman/shared/hooks/PredictionMarket/usePostComment";
import type { CommentSortOption } from "@roman/shared/types/Comment";
import type { PredictionMarketChoice } from "@roman/shared/types/PredictionMarket";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CommentLoginModal } from "./CommentLoginModal";
import { LoadMoreButton } from "./LoadMoreButton";
import { ParentComment } from "./ParentComment";

type CommentSectionProps = {
  predictionMarketId: string;
  choices: PredictionMarketChoice[];
};

export const CommentSection = ({ predictionMarketId, choices }: CommentSectionProps) => {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [sortOption, setSortOption] = useState<CommentSortOption>("MOST_REPLIED_DESC");
  const [onlyEntryAuthor, setOnlyEntryAuthor] = useState(false);

  // Use Load More button for all devices (no more device detection needed)

  const {
    error,
    data: comments,
    // Load more specific
    refetch,
    loadMore,
    loading,
    canLoadMore,
  } = useGetParentComments(predictionMarketId, sortOption, onlyEntryAuthor, false, {
    initialPerPage: 20, // Load more items per request
    loadMoreButton: true, // Use load more button for all devices
    infiniteScroll: false, // Disable automatic infinite scroll
  });

  const showOnlyMineComments = sortOption === "MOST_REPLIED_DESC";

  const {
    data: onlyMineComments,
    refetch: refetchOnlyMineComments,
    loading: isLoadingOnlyMineComments,
  } = useGetParentComments(predictionMarketId, "COMMENTED_AT_DESC", onlyEntryAuthor, true, {
    initialPerPage: 20, // Load more items per request
    loadMoreButton: true, // Use load more button for all devices
    infiniteScroll: false, // Disable automatic infinite scroll
  });

  const handleRefetch = () => {
    refetch();
    refetchOnlyMineComments();
  };

  const { error: postError } = usePostComment(
    {
      onSuccess: () => {
        handleRefetch();
        toast.success("コメントを投稿しました");
      },
      onError: (error) => {
        toast.error(error.message);
      },
    },
    (newComment) => {
      scrollToComment(newComment.id);
    },
  );

  useEffect(() => {
    if (postError) {
      toast.error(postError.message);
    }
  }, [postError]);

  return (
    <div className="flex flex-col pb-20">
      <div className="sticky top-[122px] z-10 pt-[20px] bg-white">
        <div className="flex justify-end mt-5">
          {/* Toggle to filter by entry author only. Shows a checkmark when enabled. */}
          <button
            type="button"
            onClick={() => setOnlyEntryAuthor(!onlyEntryAuthor)}
            className="text-[10px] font-medium text-dark-gray flex items-center gap-1 leading-[1.6] tracking-[-0.06em] mr-[20px]"
          >
            {onlyEntryAuthor ? <CheckBoxFilledIcon className="w-5 h-5" /> : <CheckBoxIcon className="w-5 h-5" />}
            投票者のみ
          </button>
          <SelectSortComment
            selectedSortOption={sortOption}
            setSelectedSortOption={(newSortOption) => {
              setSortOption(newSortOption);
              handleRefetch();
            }}
          />
        </div>
        <div
          className="absolute bottom-[-20px] left-0 right-0 h-[20px] pointer-events-none"
          style={{
            background: "linear-gradient(to bottom, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0))",
          }}
        />
      </div>
      <div className="flex flex-col gap-6 mt-[40.73px]">
        {comments.length === 0 && onlyMineComments.length === 0 && !loading && !isLoadingOnlyMineComments && (
          <div className="flex flex-col items-center justify-center">
            <h3 className="text-black font-bold text-[20px] leading-[1.45] mt-[66px]">コメントはまだありません</h3>
            <p className="text-dark-gray font-normal text-[13px] leading-[1.45] mt-1">
              コメントを追加して、あなたの考えをシェアしよう！
            </p>
          </div>
        )}
        {comments.length > 0 &&
          comments
            .filter((c) => !c.isDeleted)
            .filter((c) => !showOnlyMineComments || !onlyMineComments.some((cm) => cm.id === c.id))
            .slice(0, 1)
            .map((c) => (
              <ParentComment
                key={c.id}
                comment={c}
                refetchComments={handleRefetch}
                onShowLoginModal={() => setIsLoginModalOpen(true)}
                choices={choices}
                onlyEntryAuthor={onlyEntryAuthor}
              />
            ))}
        {showOnlyMineComments &&
          onlyMineComments
            .filter((c) => !c.isDeleted)
            .map((c) => (
              <ParentComment
                key={c.id}
                comment={c}
                refetchComments={handleRefetch}
                onShowLoginModal={() => setIsLoginModalOpen(true)}
                choices={choices}
                onlyEntryAuthor={onlyEntryAuthor}
              />
            ))}
        {comments
          .filter((c) => !c.isDeleted)
          .filter((c) => !showOnlyMineComments || !onlyMineComments.some((cm) => cm.id === c.id))
          .slice(1)
          .map((c) => (
            <ParentComment
              key={c.id}
              comment={c}
              refetchComments={handleRefetch}
              onShowLoginModal={() => setIsLoginModalOpen(true)}
              choices={choices}
              onlyEntryAuthor={onlyEntryAuthor}
            />
          ))}
      </div>

      {/* Load More Button for All Devices */}
      <LoadMoreButton
        onLoadMore={loadMore}
        loading={loading}
        hasMore={canLoadMore}
        error={error}
        onRetry={handleRefetch}
      />
      <CommentLoginModal open={isLoginModalOpen} setOpen={setIsLoginModalOpen} />
    </div>
  );
};
